import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { campaignQrInspectAPI } from 'api/campaign'
import { RegisterKind } from 'api/common/constants'
import { CampaignQrInspectResponse } from 'api/common/responses'
import { Status } from 'api/common/statuses'
import { ROUTE_CAMPAIGN_QR_EXPIRED, ROUTE_LOGIN, buildSummarySelectFlowUrl } from 'constants/routes'

/**
 * QR ランディング (`/summarySelect/:type/:hash`) の入口シーン (要件書 §4.14 / Rev.18 D7 / Rev.20 S46)。
 *
 * URL から hash を取得し `POST /campaign/qr/inspect` でサーバ側 AES 復号 + 期限判定を行い、
 * 結果に応じて 3 経路に分岐する:
 * - 期限切れ (`isExpired`) → /campaignQrExpired
 * - キャンペーン QR (`isCampaign && !isExpired`) → /summarySelectFlow/invite/:hash + state.isCampaign=true (Rev.20 S46)
 * - 通常 QR (それ以外) → /summarySelectFlow/:type/:hash (state なし)
 *
 * URL パラメータ不正 (hash 欠落 / RegisterKind 範囲外) は /login へ戻す。
 * フロント単独 AES 復号は鍵管理リスク回避のため非採用 (要件書 §4.14 line 814)。
 *
 * Rev.20 S46: キャンペーン QR でも概要書面交付方法選択フローを通常 QR と共有する仕様に統一。
 * 旧 `/signUpRegisterTypeSelect/:hash` 分岐は撤回し、`route state.isCampaign` で経路フラグを下流画面に伝搬する。
 */

type Destination = { url: string; options?: { state: { isCampaign: boolean } } }

const isValidRegisterKind = (type: string | undefined): boolean => type === RegisterKind.Invite || type === RegisterKind.Assignment

const resolveQrInspectDestination = (status: Status, response: CampaignQrInspectResponse | null, type: string, hash: string): Destination => {
  if (status === Status.CampaignQrExpired || response?.isExpired) {
    return { url: ROUTE_CAMPAIGN_QR_EXPIRED }
  }
  if (status === Status.Success && response?.isCampaign) {
    return { url: buildSummarySelectFlowUrl(RegisterKind.Invite, hash), options: { state: { isCampaign: true } } }
  }
  return { url: buildSummarySelectFlowUrl(type, hash) }
}

const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const { type, hash } = useParams()

  React.useEffect(() => {
    if (!hash || !isValidRegisterKind(type)) {
      navigate(ROUTE_LOGIN)
      return
    }
    campaignQrInspectAPI(hash, (status, response) => {
      const destination = resolveQrInspectDestination(status, response, type as string, hash)
      if (destination.options !== undefined) {
        navigate(destination.url, destination.options)
      } else {
        navigate(destination.url)
      }
    })
  }, [hash, type, navigate])

  return null
}

export default SceneComponent
